/**
 * useShellAttackToggles - the attack toggles, with AIM LIVING ON THE COMBATANT.
 *
 * Aim (v8.217.0) is a condition - Ah/Af, synced, undoable, chip-visible and
 * consumable by the engine. The toggle record every consumer reads is
 * DERIVED: raw local state for the scratch rack, conditions for a real
 * actor. The setter routes writes the same way, so no call site knows the
 * difference, and it survives BOTH updater forms: resolving a function
 * against the DERIVED record is what keeps aim visible to it (spreading an
 * unresolved function once wrote `undefined` into a Firestore defence doc).
 *
 * Guarded Attack's DEFENSIVE half (v8.302.0) and All-Out Attack's PRICE
 * (v8.303.0) are conditions too, written here so the pill row and the drum
 * cannot disagree. `Ao` is EDGE-TRIGGERED (v8.310.7): written when the pill
 * RISES and removed only when the pill itself LOWERS it - the swing spends
 * the +20 through the raw setter, and the price must outlive it until
 * `expireAtTurnStart` returns the reactions.
 *
 * The Guarded PILL reads the `Gd` condition back, as aim's does (2026-10-22):
 * a switch of actor lowers the raw record (`useActorSwitchReset`), and a pill
 * read from it would show a Guarded actor unguarded - and strip their `Gd` at
 * the next write.
 *
 * The called shot's ONE WRITER PAIR (v8.243.0): engage sets the penalty AND
 * the location, clear stands both down. Extracted from DeathWatchSimulator.
 */
import { useState, useRef, useMemo, useCallback } from 'react';
import { condType } from '../engine/conditions';
import { EMPTY_TOGGLES } from '../engine/actions/toggleEntries';
import { hasCondition } from './shellCombatant';
import useActorSwitchReset from './useActorSwitchReset';

/** The actor's stance conditions rewritten from a toggle record, `Ao` on its edges only. */
export function stanceConditions(conditions, resolved, hadAo) {
  const conds = withStances(conditions, resolved);
  if (!resolved.allOutAttack) return hadAo ? conds.filter((c) => condType(c) !== 'Ao') : conds;
  if (!hadAo && !hasCondition(conds, 'Ao')) conds.push('Ao');
  return conds;
}

/** The conditions with aim and guard rewritten from the record (Ah over Af). */
function withStances(conditions, resolved) {
  const conds = (conditions ?? []).filter((c) => !['Ah', 'Af', 'Gd'].includes(condType(c)));
  if (resolved.aimHalf) conds.push('Ah');
  else if (resolved.aimFull) conds.push('Af');
  if (resolved.guardedAttack) conds.push('Gd');
  return conds;
}

/** The routed setter: conditions for a real actor, raw toggles for the scratch rack. */
function useRoutedSetter(actor, updateCombatantState, attackTogglesRef, setRawAttackToggles) {
  return useCallback(
    (next) => {
      const resolved = typeof next === 'function' ? next(attackTogglesRef.current) : next;
      if (!actor) {
        setRawAttackToggles(resolved);
        return;
      }
      const hadAo = !!attackTogglesRef.current.allOutAttack;
      updateCombatantState(actor.id, (st) => ({ ...st, conditions: stanceConditions(st.conditions, resolved, hadAo) }));
      setRawAttackToggles({ ...resolved, aimHalf: false, aimFull: false });
    },
    [actor, updateCombatantState, attackTogglesRef, setRawAttackToggles],
  );
}

/**
 * @param {object} args - actor, actorConds (the actor's live conditions),
 *   updateCombatantState, setTargetLocation.
 */
/** The record every consumer reads: the raw one for the scratch rack, aim and Guarded off the actor's conditions. */
function useDerivedToggles(actor, actorConds, rawAttackToggles) {
  return useMemo(() => {
    if (!actor) return rawAttackToggles;
    return {
      ...rawAttackToggles,
      aimHalf: hasCondition(actorConds, 'Ah'),
      aimFull: hasCondition(actorConds, 'Af'),
      guardedAttack: hasCondition(actorConds, 'Gd'),
    };
  }, [actor, rawAttackToggles, actorConds]);
}

export default function useShellAttackToggles({ actor, actorConds, updateCombatantState, setTargetLocation }) {
  const [rawAttackToggles, setRawAttackToggles] = useState(EMPTY_TOGGLES);
  const attackToggles = useDerivedToggles(actor, actorConds, rawAttackToggles);
  // A new actor starts clean: the per-attack pills and the called location go down with the switch.
  useActorSwitchReset(actor?.id ?? null, () => {
    setRawAttackToggles(EMPTY_TOGGLES);
    setTargetLocation('auto');
  });
  const attackTogglesRef = useRef(attackToggles);
  attackTogglesRef.current = attackToggles;
  const setAttackToggles = useRoutedSetter(actor, updateCombatantState, attackTogglesRef, setRawAttackToggles);
  const engageCalledShot = useCallback(
    (locId) => {
      setAttackToggles({ ...attackTogglesRef.current, calledShot: true });
      setTargetLocation(locId);
    },
    [setAttackToggles, setTargetLocation],
  );
  const clearCalledShot = useCallback(() => {
    setAttackToggles({ ...attackTogglesRef.current, calledShot: false });
    setTargetLocation('auto');
  }, [setAttackToggles, setTargetLocation]);
  return { rawAttackToggles, setRawAttackToggles, attackToggles, setAttackToggles, engageCalledShot, clearCalledShot };
}
